import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { useToasts } from 'react-toast-notifications';
import styles from './CarAlertsTab.module.scss';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, MAX_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import EyeIcon from 'assets/svg/eye-solid.svg';
import Logger from 'common/services/Logger';
import Loading from 'common/services/Loading';
import Popover from 'common/components/popover/Popover';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import InfoCard from 'common/components/infoCard/InfoCard';
import DataTableWithSidebar from 'common/components/dataTable/DataTableWithSidebar';
import { OrderData } from 'common/components/dataTable/models';
import useBidirectionalScroll from 'common/hooks/useBidirectionalScroll';
import InputSearch from 'common/components/inputSearch/InputSearch';
import AlertsFilters, { Filters } from 'screens/alerts/components/alertsFilters/AlertsFilters';
import useAlertsTableColumns from 'screens/alerts/hooks/useAlertsTableColumns';
import AlertsService from 'api/alerts/AlertsService';
import { AlertsSearchCriteria } from 'api/alerts/models/AlertsSearchCriteria';
import { AlertsListItemDto } from 'api/alerts/models/AlertsListItemDto';
import { AlertsTotalsDto } from 'api/alerts/models/AlertsTotalsDto';
import { getAlertTypeColor, getSubjectTypeColor } from 'common/utils/AlertsUtils';
import { updateTotals } from 'store/alerts/action';
import { Reducers } from 'store/types';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { VehicleDto } from 'api/vehicles/models/VehicleDto';
import colors from 'styles/export/colors.module.scss';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';

const INFO_CARD_COLORS = [
    colors.counterTeal1,
    colors.counterTeal2,
    colors.counterBlue1,
];

type Props = {
    vehicle?: VehicleDto;
    tourReRender: () => void;
};

const CarAlertsTab = ({ vehicle, tourReRender }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const dispatch = useDispatch();
    const history = useHistory();
    const { vehicleId } = useParams<{ vehicleId: string, itemType: string, itemId: string, tabId: string, type: string }>();
    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasAlertsWritePolicy = UsersService.hasPolicies(loggedUser?.policies || [], ['SETTINGUP_ALERTS_READ']);

    const editable = vehicle != undefined && vehicle.active;
    const CACHE_KEY = 'vehicle-alerts-' + vehicleId;

    // Bidirectional scroll
    const bidirectionalScroll = useBidirectionalScroll({
        cacheKey: CACHE_KEY,
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
    });

    const {
        loadedPageRange,
        isLoadingPrevious,
        scrollToRowId: scrollToItemId,
        shouldLoadAdjacentPages,
        setLoadedPageRange,
        setIsLoadingPrevious,
        resetScrollState,
        calculatePageFromIndex,
        prepareLoadPrevious,
        adjustScrollAfterPrepend,
        finishRestoration,
        initiateRestoration,
        getHasMore,
        getHasMorePrevious,
        getOnLoadMore,
        getOnLoadPrevious,
    } = bidirectionalScroll;

    const [criteria, setCriteria] = useState<AlertsSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'a.created_date',
        orderBy: 'desc',
        vehicleId,
        responsibleId: loggedUser?.id,
    });

    const [alerts, setAlerts] = useState<AlertsListItemDto[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [filters, setFilters] = useState<Filters>({ responsibleId: criteria.responsibleId, responsibleName: loggedUser?.realName });
    const [filtersTotal, setFiltersTotal] = useState(0);
    const [totals, setTotals] = useState<AlertsTotalsDto>({ totalNotRead: 0, totalNotReadOwn: 0, totalRead: 0, totalTreated: 0, totalNonTreated: 0, totalInterventionUrgent: 0, totalInformation: 0, totalIntervention: 0 });
    const [reRenderSearch, setReRenderSearch] = useState<number>(0);

    // Order state for DataTable visual indicator
    const getFieldFromOrderColumn = (oc: string): keyof AlertsListItemDto => {
        const mapping: Record<string, string> = {
            'anu.real_name': 'userName',
            'a.created_date': 'createdDate',
            'a.type': 'type',
            'a.subject': 'subject',
            'a.message': 'message',
            'au.read_date is null': 'readDate',
        };
        return (mapping[oc] ?? 'createdDate') as keyof AlertsListItemDto;
    };

    const [order, setOrder] = useState<OrderData<AlertsListItemDto>>({
        field: 'createdDate',
        orderColumn: 'a.created_date',
        isOrderAsc: false,
    });

    const isFirstLoad = useRef(true);
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);
    const previousCriteria = useRef<AlertsSearchCriteria>(criteria);
    const countersLoaded = useRef(false);

    // Shared table columns (without vehicle columns)
    const tableColumns = useAlertsTableColumns({ hideVehicleColumns: true, hasAlertsWritePolicy, editable });

    const fetchAllForExport = useCallback(async () => {
        const result = await AlertsService.getList({
            ...criteria,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
        });
        result.items.forEach(a => {
            a.colorType = getAlertTypeColor(a.type);
            a.color = getSubjectTypeColor(a.subject);
        });
        return result.items;
    }, [criteria]);

    // ─── Data fetching ───────────────────────────────────────────────

    const getData = async (isLoadMore = false, shouldUpdateCounters = false) => {
        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            saveOrdernationFilters();

            const alertsItems = await AlertsService.getList({ ...criteria });

            alertsItems.items.forEach(a => {
                a.colorType = getAlertTypeColor(a.type);
                a.color = getSubjectTypeColor(a.subject);
            });

            setTotalItems(alertsItems.totalItems);

            if (isLoadMore) {
                setAlerts(prev => [...prev, ...alertsItems.items]);
                setLoadedPageRange(prev => ({ ...prev, max: criteria.page }));
            } else {
                setAlerts(alertsItems.items);
            }

            if (shouldUpdateCounters) {
                await getCounters();
                countersLoaded.current = true;
            }

            Loading.hide();
            setIsLoadingMore(false);

            // Bidirectional scroll restoration
            if (shouldLoadAdjacentPages.current) {
                shouldLoadAdjacentPages.current = false;
                const currentPage = criteria.page;
                const totalPages = Math.ceil(alertsItems.totalItems / (criteria.itemsPerPage || DEFAULT_PAGINATION_ITEMS_PER_PAGE));

                if (currentPage > 1) {
                    try {
                        const prevCriteria = { ...criteria, page: currentPage - 1 };
                        const prevResult = await AlertsService.getList(prevCriteria);
                        prevResult.items.forEach(a => {
                            a.colorType = getAlertTypeColor(a.type);
                            a.color = getSubjectTypeColor(a.subject);
                        });
                        setAlerts(prev => [...prevResult.items, ...prev]);
                        setLoadedPageRange(prev => ({ ...prev, min: currentPage - 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.ALERTS, `Couldn't load previous page`, e);
                    }
                }

                if (currentPage < totalPages) {
                    try {
                        const nextCriteria = { ...criteria, page: currentPage + 1 };
                        const nextResult = await AlertsService.getList(nextCriteria);
                        nextResult.items.forEach(a => {
                            a.colorType = getAlertTypeColor(a.type);
                            a.color = getSubjectTypeColor(a.subject);
                        });
                        setAlerts(prev => [...prev, ...nextResult.items]);
                        setLoadedPageRange(prev => ({ ...prev, max: currentPage + 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.ALERTS, `Couldn't load next page`, e);
                    }
                }

                finishRestoration();
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.ALERTS, `Couldn't get alerts list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Loading.hide();
            setIsLoadingMore(false);
            resetScrollState();
        } finally {
            tourReRender();
        }
    };

    const getCounters = async () => {
        try {
            const _totals = await AlertsService.getTotals(criteria);
            setTotals(_totals);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.ALERTS, `Couldn't get alerts totals`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    };

    // ─── Effects ─────────────────────────────────────────────────────

    useEffect(() => {
        if (isFirstLoad.current) {
            loadFilters();
        } else {
            const isLoadMore = criteria.page > loadedPageRange.max && alerts.length > 0;

            const prev = previousCriteria.current;
            const onlyOrderOrPageChanged =
                prev.startDate === criteria.startDate &&
                prev.endDate === criteria.endDate &&
                prev.type === criteria.type &&
                prev.reading === criteria.reading &&
                prev.allIn === criteria.allIn;

            const shouldLoadCounters = !countersLoaded.current || !onlyOrderOrPageChanged;
            getData(isLoadMore, shouldLoadCounters);
            previousCriteria.current = criteria;
        }
    }, [criteria]);

    useEffect(() => {
        if (!isLoadingPrevious) {
            adjustScrollAfterPrepend();
        }
    }, [alerts, isLoadingPrevious, adjustScrollAfterPrepend]);

    // ─── Filters & cache ─────────────────────────────────────────────

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem(CACHE_KEY);
        if (cachedFilters != null) {
            const f = JSON.parse(cachedFilters);
            f.vehicleId = vehicleId;

            if (f.scrollToItemId) {
                const startPage = initiateRestoration({
                    page: f.page || 1,
                    scrollToItemId: f.scrollToItemId,
                });

                const cleanedFilters = { ...f, scrollToItemId: undefined, page: 1 };
                localStorage.setItem(CACHE_KEY, JSON.stringify(cleanedFilters));
                setFilters(cleanedFilters);
                updateTotalFilters(cleanedFilters);

                setCriteria(prev => ({
                    ...prev,
                    vehicleId,
                    type: f.type,
                    reading: f.reading,
                    responsibleId: f.responsibleId,
                    startDate: f.startDate,
                    endDate: f.endDate,
                    orderBy: f.orderBy,
                    orderColumn: f.orderColumn,
                    allIn: f.allIn,
                    page: startPage,
                    itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                }));

                if (f.orderBy && f.orderColumn) {
                    setOrder({
                        field: getFieldFromOrderColumn(f.orderColumn),
                        orderColumn: f.orderColumn,
                        isOrderAsc: f.orderBy === 'asc',
                    });
                }

                setReRenderSearch(r => r + 1);
                isFirstLoad.current = false;
                return;
            }

            f.scrollToItemId = undefined;
            f.page = 1;
            resetScrollState();
            localStorage.setItem(CACHE_KEY, JSON.stringify(f));
            onChangeFilters(f, true);
        } else {
            getData(false, true);
        }

        isFirstLoad.current = false;
    };

    const saveFiltersOnCache = (f: Filters) => {
        setFilters(prev => ({ ...prev, ...f }));
        if ((f as any).scrollToItemId && (f as any).page) {
            localStorage.setItem(CACHE_KEY, JSON.stringify(f));
        } else {
            const { page, scrollToItemId, ...filtersWithoutScrollData } = f as any;
            localStorage.setItem(CACHE_KEY, JSON.stringify(filtersWithoutScrollData));
        }
    };

    const saveOrdernationFilters = () => {
        const newFilters = { ...filters };
        newFilters.vehicleId = criteria.vehicleId;
        newFilters.allIn = criteria.allIn;
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        saveFiltersOnCache(newFilters);
    };

    const onChangeFilters = (_filters: Filters, isOnLoad?: boolean) => {
        updateTotalFilters(_filters);
        saveFiltersOnCache(_filters);

        if (!isOnLoad) {
            setAlerts([]);
            resetScrollState();
            setTotalItems(0);
        }

        const effectiveOrderColumn = _filters.orderColumn || 'a.created_date';
        const effectiveOrderBy = _filters.orderBy || 'desc';

        setCriteria(prev => ({
            ...prev,
            vehicleId,
            type: _filters.type,
            reading: _filters.reading,
            startDate: _filters.startDate,
            endDate: _filters.endDate,
            orderBy: effectiveOrderBy,
            orderColumn: effectiveOrderColumn,
            allIn: _filters.allIn,
            page: 1,
        }));

        setOrder({
            field: getFieldFromOrderColumn(effectiveOrderColumn),
            orderColumn: effectiveOrderColumn,
            isOrderAsc: effectiveOrderBy === 'asc',
        });

        setReRenderSearch(r => r + 1);
    };

    const updateTotalFilters = (_filters: Filters) => {
        let total = 0;
        if (_filters.type) total++;
        if (_filters.startDate) total++;
        if (_filters.endDate) total++;
        if (_filters.reading !== undefined) total++;
        setFiltersTotal(total);
    };

    // ─── Infinite scroll handlers ────────────────────────────────────

    const loadMore = useCallback(() => {
        if (isLoadingMore || alerts.length >= totalItems) return;
        const nextPage = loadedPageRange.max + 1;
        setCriteria(prev => ({ ...prev, page: nextPage }));
    }, [isLoadingMore, alerts.length, totalItems, loadedPageRange.max]);

    const loadPrevious = useCallback(async () => {
        if (isLoadingPrevious || loadedPageRange.min <= 1) return;

        setIsLoadingPrevious(true);
        const prevPage = loadedPageRange.min - 1;

        try {
            prepareLoadPrevious();

            const prevCriteria: AlertsSearchCriteria = {
                ...criteria,
                page: prevPage,
                itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
            };
            const result = await AlertsService.getList(prevCriteria);

            result.items.forEach(a => {
                a.colorType = getAlertTypeColor(a.type);
                a.color = getSubjectTypeColor(a.subject);
            });

            setAlerts(prev => [...result.items, ...prev]);
            setLoadedPageRange(prev => ({ ...prev, min: prevPage }));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.ALERTS, `Couldn't get previous alerts page`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setIsLoadingPrevious(false);
        }
    }, [isLoadingPrevious, loadedPageRange.min, criteria, addToast, t]);

    // ─── Order change ────────────────────────────────────────────────

    const handleOrderChange = useCallback((orderData: OrderData<AlertsListItemDto>) => {
        setOrder(orderData);
        setAlerts([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({
            ...prev,
            page: 1,
            orderBy: orderData.isOrderAsc ? 'asc' : 'desc',
            orderColumn: orderData.orderColumn || (orderData.field as string),
        }));
    }, [resetScrollState]);

    // ─── Search ──────────────────────────────────────────────────────

    const debounced = useDebouncedCallback((value: string) => {
        if (value === criteria.allIn) return;
        setAlerts([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({ ...prev, page: 1, allIn: value }));
    }, 500);

    // ─── Counter click ───────────────────────────────────────────────

    const getDataCounter = async (mode: boolean | undefined) => {
        try {
            Loading.show();

            filters.reading = filters.readingName = undefined;
            updateTotalFilters(filters);
            setFilters(prev => ({ ...prev, reading: undefined, readingName: undefined }));

            setAlerts([]);
            resetScrollState();
            setTotalItems(0);

            const alertsItems = await AlertsService.getList({ ...criteria, reading: mode, page: 1 });

            alertsItems.items.forEach(a => {
                a.colorType = getAlertTypeColor(a.type);
                a.color = getSubjectTypeColor(a.subject);
            });

            setTotalItems(alertsItems.totalItems);
            setAlerts(alertsItems.items);

            const _totals = await AlertsService.getTotals({ ...criteria, reading: undefined });
            setTotals(_totals);

            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.ALERTS, `Couldn't get alerts list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Loading.hide();
        }
    };

    // ─── Navigation ──────────────────────────────────────────────────

    const navigateUrl = (row: AlertsListItemDto) => {
        switch (row.type) {
            case 'VERIFICATION_NEW_ALERT': return `/vehicles/details/${row.vehicleId}/verifications/details/${row.referenceId}`;
            case 'ANNOTATION_INTERVENTION_TYPE_INFORMATION_ALERT': return `/vehicles/details/${row.vehicleId}/verifications/details/${row.verificationId}`;
            case 'ANNOTATION_INTERVENTION_TYPE_INTERVENTION_ALERT': return `/vehicles/details/${row.vehicleId}/verifications/details/${row.verificationId}`;
            case 'ANNOTATION_INTERVENTION_TYPE_URGENT_ALERT': return `/vehicles/details/${row.vehicleId}/verifications/details/${row.verificationId}`;
            case 'MAINTENANCE_NEW_ALERT': return `/vehicles/details/${row.vehicleId}/maintenances/details/${row.referenceId}`;
            case 'MAINTENANCE_PLANNED_ALERT': return `/vehicles/details/${row.vehicleId}/maintenances/details/${row.referenceId}`;
            case 'RESERVATION_NEW_ALERT': return `/reservations/search/${row.referenceId}`;
            case 'EXPENSE_NEW_ALERT': return `/vehicles/details/${row.vehicleId}/costs/details/${row.referenceId}`;
            case 'EXPENSE_TREATED_ALERT': return `/vehicles/details/${row.vehicleId}/costs/details/${row.referenceId}`;
            case 'EXPENSE_NOT_TREATED_ALERT': return `/vehicles/details/${row.vehicleId}/costs/details/${row.referenceId}`;
            case 'EXPENSE_REFUND_APPROVED_ALERT': return `/vehicles/details/${row.vehicleId}/costs/details/${row.referenceId}`;
            case 'EXPENSE_REFUND_REFUSED_ALERT': return `/vehicles/details/${row.vehicleId}/costs/details/${row.referenceId}`;
            case 'CONTRACT_INSURANCE_ALERT': return `/vehicles/details/${row.vehicleId}/contract/details/insurance/details/${row.referenceId}`;
            case 'CONTRACT_MAINTENANCE_ALERT': return `/vehicles/details/${row.vehicleId}/contract/details/maintenance/details/${row.referenceId}`;
            case 'CONTRACT_USE_ALERT': return `/vehicles/details/${row.vehicleId}/contract/details/use/details/${row.referenceId}`;
            case 'EXPENSE_FILE_INVALID_NEW_ALERT': return `/import-charges/details/${row.referenceId}`;
            case 'EXPENSE_FILE_PROCESSED_NEW_ALERT': return `/import-charges/details/${row.referenceId}`;
            default: return '';
        }
    };

    const readAlert = async (row: AlertsListItemDto, _index: number, event: any) => {
        // Save scroll position
        const itemIndex = alerts.findIndex(a => a.id === row.id);
        const actualPage = calculatePageFromIndex(itemIndex);
        const newFilters = { ...filters, page: actualPage, scrollToItemId: row.id, orderBy: criteria.orderBy, orderColumn: criteria.orderColumn };
        saveFiltersOnCache(newFilters);

        if (row.readDate) {
            const url = navigateUrl(row);
            if (url) {
                if (event.ctrlKey) window.open(url, '_blank');
                history.push(url);
            }
            return;
        }

        try {
            Loading.show();
            if (hasAlertsWritePolicy) {
                await AlertsService.read(row.id);
                dispatch(updateTotals());
            }

            const url = navigateUrl(row);
            if (url) {
                if (event.ctrlKey) window.open(url, '_blank');
                history.push(url);
            }
            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.ALERTS, `Couldn't read alert`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Loading.hide();
        }
    };

    // ─── Counters ────────────────────────────────────────────────────

    const renderCounterBoxes = () => (
        <div className={styles.rowCounters}>
            <InfoCard
                label={t('common.total') as string}
                value={(totals.totalRead + totals.totalNotRead) ?? 0}
                icon={EyeIcon}
                backgroundColor={INFO_CARD_COLORS[0]}
                className={styles.col2}
                onClick={() => getDataCounter(undefined)}
            />
            <InfoCard
                label={t('alerts.list.reads') as string}
                value={totals.totalRead ?? 0}
                icon={EyeIcon}
                backgroundColor={INFO_CARD_COLORS[1]}
                className={styles.col2}
                onClick={() => getDataCounter(true)}
            />
            <InfoCard
                label={t('alerts.list.not_reads') as string}
                value={totals.totalNotRead ?? 0}
                icon={EyeIcon}
                backgroundColor={INFO_CARD_COLORS[2]}
                className={styles.col2}
                onClick={() => getDataCounter(false)}
            />
        </div>
    );

    // ─── Render ──────────────────────────────────────────────────────

    return (
        <div className={styles.listWrapper}>
            <div className={styles.tabHeader}>
                <div>
                    <InputSearch
                        key={reRenderSearch}
                        inputContainerClassName={styles.inputContainer}
                        onChangeValue={debounced}
                        defaultValue={filters.allIn || ''}
                        placeholder={t('alerts.list.search_vehicle_page') as string}
                    />
                </div>
                <div className={styles.rowFlex}>
                    <Popover
                        contentContainerClassName={styles.filtersPopoverContainer}
                        containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                        positions={['bottom', 'left']}
                        align={'end'}
                        onClickOutside={() => updateTotalFilters(filters)}
                        content={setIsPopoverOpen => <AlertsFilters
                            filters={filters}
                            onFilter={_filters => { setIsPopoverOpen(false); onChangeFilters(_filters) }}
                            onChange={_filters => updateTotalFilters(_filters)}
                            hideVehicleFilter={true}
                            isVehicle={true}
                        />}
                    >
                        {(isPopoverOpen, setIsPopoverOpen) => (
                            <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                {filtersTotal > 0 &&
                                    <div className={styles.counterList}><div className={styles.counterNumber}>{filtersTotal}</div></div>
                                }
                            </ScreenHeaderButton>
                        )}
                    </Popover>
                    <ExportListingButton exportRef={exportListingRef} />
                </div>
            </div>

            <div className={styles.countersSection}>
                {renderCounterBoxes()}
            </div>

            <div className={styles.listTblWrapper}>
                <DataTableWithSidebar
                    cacheKey={CACHE_KEY + '_TABLE'}
                    columns={tableColumns}
                    rows={alerts}
                    defaultOrder={order}
                    onRowClick={readAlert}
                    onOrderChange={handleOrderChange}
                    onRowId={row => row.id}
                    scrollToRowId={scrollToItemId}
                    infiniteScroll={{
                        isLoading: isLoadingMore,
                        hasMore: getHasMore(alerts.length, totalItems),
                        onLoadMore: getOnLoadMore(loadMore),
                        hasMorePrevious: getHasMorePrevious(),
                        isLoadingPrevious,
                        onLoadPrevious: getOnLoadPrevious(loadPrevious),
                    }}
                    exportExcel={{
                        fetchAll: fetchAllForExport,
                        fileName: t('alerts.title') as string,
                    }}
                    exportRef={exportListingRef}
                    hideExportInSidebar={true}
                />
            </div>
        </div>
    );
};

export default CarAlertsTab;
